import { useAppSelector } from '@/hooks/useAppSelector';
import { Plan, Charge, Discount } from '@/components/company/types/planTypes';

/**
 * Helper hook for working with GraphQL data in the Redux store
 * Provides utility functions for plans, charges, discounts, and screenshots
 */
export const useGraphQLHelper = () => {
  // Get GraphQL data from Redux state
  const graphQLData = useAppSelector(state => state.graphql.data);
  
  /**
   * Check if a plan exists in a specific version
   */
  const isPlanInVersion = (planId: string | null | undefined, version: string): boolean => {
    if (!planId || !version || !graphQLData?.pages) return false;
    
    // Check each page for the specific version's quarters
    for (const page of graphQLData.pages) {
      if (!page.quarters) continue;
      
      // First, check exact version match
      let quarter = page.quarters.find(q => q.version === version);
      
      // If no match, try to find by quarter value - for cases where version ID doesn't match exactly
      if (!quarter) {
        // Extract quarter value from version (e.g., "20230416" -> "2023Q2")
        const yearFromVersion = version.substring(0, 4);
        const monthFromVersion = parseInt(version.substring(4, 6));
        let inferredQuarter = "";
        
        // Map month to quarter
        if (monthFromVersion >= 1 && monthFromVersion <= 3) {
          inferredQuarter = `${yearFromVersion}Q1`;
        } else if (monthFromVersion >= 4 && monthFromVersion <= 6) {
          inferredQuarter = `${yearFromVersion}Q2`;
        } else if (monthFromVersion >= 7 && monthFromVersion <= 9) {
          inferredQuarter = `${yearFromVersion}Q3`;
        } else {
          inferredQuarter = `${yearFromVersion}Q4`;
        }
        
        // Find the quarter object that matches the inferred quarter
        quarter = page.quarters.find(q => q.quarter === inferredQuarter);
      }
      
      // Check if we found a quarter and if the planId exists in the ids array
      if (quarter && quarter.ids && quarter.ids.includes(planId)) {
        return true;
      }
      
      // Finally, check if the plan exists in the scrapes for this version
      if (page.scrapes) {
        const scrape = page.scrapes.find(s => s.version === version);
        if (scrape) {
          // Try to find a quarter matching the scrape's quarter
          const scrapeQuarter = page.quarters.find(q => q.quarter === scrape.quarter);
          if (scrapeQuarter && scrapeQuarter.ids && scrapeQuarter.ids.includes(planId)) {
            return true;
          }
        }
      }
    }
    
    return false;
  };
  
  /**
   * Check if a charge exists in a specific version for a given plan
   */
  const isChargeInVersion = (planId: string | null | undefined, chargeId: string | null | undefined, version: string): boolean => {
    if (!graphQLData || !graphQLData.pages || !planId || !chargeId || !version) return false;
    
    // First, check if the charge has a direct versions array that includes this version
    // This is the data structure we update with our Redux action
    for (const page of graphQLData.pages) {
      if (!page.plans) continue;
      
      for (const plan of page.plans) {
        if (plan.id !== planId || !plan.charges) continue;
        
        // Find the charge in this plan
        const charge = plan.charges.find(c => c.id === chargeId);
        
        // Check if the charge has a versions array that includes this version
        if (charge && charge.versions && Array.isArray(charge.versions) && charge.versions.includes(version)) {
          return true;
        }
      }
    }
    
    // Fallback: Check each page for the specific version's quarters
    // This is the legacy data structure
    for (const page of graphQLData.pages) {
      if (!page.quarters) continue;
      
      // First, check exact version match
      let quarter = page.quarters.find(q => q.version === version);
      
      // If no match, try to find by quarter value - for cases where version ID doesn't match exactly
      if (!quarter) {
        // Extract quarter value from version (e.g., "20230416" -> "2023Q2")
        const yearFromVersion = version.substring(0, 4);
        const monthFromVersion = parseInt(version.substring(4, 6));
        let inferredQuarter = "";
        
        // Map month to quarter
        if (monthFromVersion >= 1 && monthFromVersion <= 3) {
          inferredQuarter = `${yearFromVersion}Q1`;
        } else if (monthFromVersion >= 4 && monthFromVersion <= 6) {
          inferredQuarter = `${yearFromVersion}Q2`;
        } else if (monthFromVersion >= 7 && monthFromVersion <= 9) {
          inferredQuarter = `${yearFromVersion}Q3`;
        } else {
          inferredQuarter = `${yearFromVersion}Q4`;
        }
        
        // Find the quarter object that matches the inferred quarter
        quarter = page.quarters.find(q => q.quarter === inferredQuarter);
      }
      
      // Check if we found a quarter and if the chargeId exists in the ids array
      if (quarter && quarter.ids && quarter.ids.includes(chargeId)) {
        return true;
      }
      
      // Finally, check if the charge exists in the scrapes for this version
      if (page.scrapes) {
        const scrape = page.scrapes.find(s => s.version === version);
        if (scrape) {
          // Try to find a quarter matching the scrape's quarter
          const scrapeQuarter = page.quarters.find(q => q.quarter === scrape.quarter);
          if (scrapeQuarter && scrapeQuarter.ids && scrapeQuarter.ids.includes(chargeId)) {
            return true;
          }
        }
      }
    }
    
    return false;
  };
  
  /**
   * Check if a discount exists in a specific version for a given plan
   */
  const isDiscountInVersion = (planId: string | null | undefined, discountId: string | null | undefined, version: string): boolean => {
    if (!planId || !discountId || !version || !graphQLData?.pages) {
      console.log(`isDiscountInVersion check failed: missing data, planId=${planId}, discountId=${discountId}, version=${version}`);
      return false;
    }
    
    // Important: The current GraphQL schema doesn't include discounts in the query
    // However, discount IDs might be included in the quarters.ids array
    
    // Check each page for the specific version's quarters
    for (const page of graphQLData.pages) {
      if (!page.quarters) continue;
      
      // First, check exact version match
      let quarter = page.quarters.find(q => q.version === version);
      
      // If no match, try to find by quarter value - for cases where version ID doesn't match exactly
      if (!quarter) {
        // Extract quarter value from version (e.g., "20230416" -> "2023Q2")
        const yearFromVersion = version.substring(0, 4);
        const monthFromVersion = parseInt(version.substring(4, 6));
        let inferredQuarter = "";
        
        // Map month to quarter
        if (monthFromVersion >= 1 && monthFromVersion <= 3) {
          inferredQuarter = `${yearFromVersion}Q1`;
        } else if (monthFromVersion >= 4 && monthFromVersion <= 6) {
          inferredQuarter = `${yearFromVersion}Q2`;
        } else if (monthFromVersion >= 7 && monthFromVersion <= 9) {
          inferredQuarter = `${yearFromVersion}Q3`;
        } else {
          inferredQuarter = `${yearFromVersion}Q4`;
        }
        
        // Find the quarter object that matches the inferred quarter
        quarter = page.quarters.find(q => q.quarter === inferredQuarter);
      }
      
      // Check if we found a quarter and if the discountId exists in the ids array
      if (quarter && quarter.ids && quarter.ids.includes(discountId)) {
        console.log(`Discount ${discountId} found in version ${version} or its quarter`);
        return true;
      }
      
      // Finally, check if the discount exists in the scrapes for this version
      if (page.scrapes) {
        const scrape = page.scrapes.find(s => s.version === version);
        if (scrape) {
          // Try to find a quarter matching the scrape's quarter
          const scrapeQuarter = page.quarters.find(q => q.quarter === scrape.quarter);
          if (scrapeQuarter && scrapeQuarter.ids && scrapeQuarter.ids.includes(discountId)) {
            console.log(`Discount ${discountId} found in scrape with quarter ${scrape.quarter}`);
            return true;
          }
        }
      }
    }
    
    return false;
  };
  
  /**
   * Check if a screenshot is invalid for a specific version
   */
  const isScreenshotInvalid = (version: string): boolean => {
    if (!version || !graphQLData?.pages) return false;
    
    // Check all pages for invalid screenshots in this version
    for (const page of graphQLData.pages) {
      // Check quarters data
      const quarters = page.quarters || [];
      
      // First, check exact version match
      let quarter = quarters.find(q => q.version === version);
      
      // If no direct match, try to infer the quarter from the version
      if (!quarter) {
        // Extract quarter value from version (e.g., "20230416" -> "2023Q2")
        const yearFromVersion = version.substring(0, 4);
        const monthFromVersion = parseInt(version.substring(4, 6));
        let inferredQuarter = "";
        
        // Map month to quarter
        if (monthFromVersion >= 1 && monthFromVersion <= 3) {
          inferredQuarter = `${yearFromVersion}Q1`;
        } else if (monthFromVersion >= 4 && monthFromVersion <= 6) {
          inferredQuarter = `${yearFromVersion}Q2`;
        } else if (monthFromVersion >= 7 && monthFromVersion <= 9) {
          inferredQuarter = `${yearFromVersion}Q3`;
        } else {
          inferredQuarter = `${yearFromVersion}Q4`;
        }
        
        // Find the quarter object that matches the inferred quarter
        quarter = quarters.find(q => q.quarter === inferredQuarter);
      }
      
      // If quarter exists but no screenshot_url or it's explicitly marked unverified
      if (quarter && (!quarter.screenshot_url || quarter.verified === false)) {
        return true;
      }
      
      // Also check scrapes data
      const scrapes = page.scrapes || [];
      
      // Check direct match in scrapes
      let scrape = scrapes.find(s => s.version === version);
      
      // If no direct match, try to find by quarter
      if (!scrape && quarter) {
        // Find a scrape with the matching quarter
        scrape = scrapes.find(s => s.quarter === quarter.quarter);
      }
      
      // If scrape exists but no screenshot_url or it's explicitly marked unverified
      if (scrape && (!scrape.screenshot_url || scrape.verified === false)) {
        return true;
      }
    }
    
    return false;
  };
  
  /**
   * Get all unique charges for a specific plan
   */
  const getUniqueChargesForPlan = (planId: string | null | undefined): Charge[] => {
    if (!planId || !graphQLData?.pages) return [];
    
    const uniqueCharges = new Map<string, Charge>();
    
    // Check each page for the plan and collect unique charges
    for (const page of graphQLData.pages) {
      if (!page.plans) continue;
      
      // Find the plan
      const plan = page.plans.find(p => p.id === planId);
      if (!plan || !plan.charges) continue;
      
      // Add all charges to our map, using id as the key to ensure uniqueness
      for (const charge of plan.charges) {
        if (charge.id && !uniqueCharges.has(charge.id)) {
          uniqueCharges.set(charge.id, charge);
        }
      }
    }
    
    return Array.from(uniqueCharges.values());
  };
  
  /**
   * Get unique discounts for a given plan
   */
  const getUniqueDiscountsForPlan = (planId: string | null | undefined): Discount[] => {
    if (!planId || !graphQLData?.pages) return [];
    
    const uniqueDiscountsMap = new Map<string, Discount>();
    
    // Check each page for the plan
    for (const page of graphQLData.pages) {
      if (!page.plans) continue;
      
      // Find the plan
      const plan = page.plans.find(p => p.id === planId);
      if (!plan || !plan.discounts) continue;
      
      // Add each discount to our map to deduplicate
      plan.discounts.forEach(discount => {
        if (discount && discount.id) {
          uniqueDiscountsMap.set(discount.id, discount);
        }
      });
    }
    
    const discounts = Array.from(uniqueDiscountsMap.values());
    console.log(`Found ${discounts.length} discounts for plan ${planId}`);
    return discounts;
  };

  /**
   * Get a plan by its ID
   */
  const getPlanById = (planId: string | null | undefined): Plan | null => {
    if (!planId || !graphQLData?.pages) return null;
    
    // Check each page for the plan
    for (const page of graphQLData.pages) {
      if (!page.plans) continue;
      
      // Find and return the plan
      const plan = page.plans.find(p => p.id === planId);
      if (plan) return plan;
    }
    
    return null;
  };

  /**
   * Get all plans from all pages
   */
  const getAllPlans = (): Plan[] => {
    if (!graphQLData?.pages) return [];
    
    const allPlans: Plan[] = [];
    
    // Collect plans from all pages
    for (const page of graphQLData.pages) {
      if (page.plans && page.plans.length > 0) {
        allPlans.push(...page.plans);
      }
    }
    
    return allPlans;
  };
  
  return {
    isPlanInVersion,
    isChargeInVersion,
    isDiscountInVersion,
    isScreenshotInvalid,
    getUniqueChargesForPlan,
    getUniqueDiscountsForPlan,
    getPlanById,
    getAllPlans,
    graphQLData
  };
};

export default useGraphQLHelper;
